import { ChangeDetectionStrategy, Component, computed, input, output, signal, WritableSignal } from '@angular/core';
import { ProgressTypeDb } from 'lib/cosmos/progress-type/progressTypeDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { guidHelper } from 'lib/helpers/guid-helper.class.js';
import { ISODate } from 'lib/time/index.js';
import { SelectItemField } from 'lib/ui/form/fields/select-item-field.component.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { DateTime } from 'luxon';
import { map, of } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { progressHelper } from './progress-helper.class.js';

type FormInput = {
	readonly id: string;
	readonly name: string;
	readonly subtitle: string | undefined;
	readonly value: WritableSignal<number | undefined>;
	readonly assignedProgressType: UserDb.IUserAssignedProgressType;
};

@Component({
	templateUrl: './submit-progress-measurements.component.html',
	selector: 'mod-submit-progress-measurements',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SubmitProgressMeasurementsComponent extends TypeCoreComponent {
	public readonly isInDialog = input.required<boolean>();
	public readonly assignedUserProgressTypes = input.required<readonly UserDb.IUserAssignedProgressType[]>();
	public readonly allProgressTypes = input.required<readonly ProgressTypeDb.IProgressTypeItem[]>();

	public readonly save = output<readonly UserDb.IUserAssignedProgressType[]>();

	private readonly _availableDatesCount: number = 7;

	public readonly selectedDate = signal<ISODate>(this.getDateValueToStore(this.dependencies.timeService.now()));

	public readonly availableDatesSelectionList = computed<SelectItemField<DateTime>[]>(() => {
		const selectItems: SelectItemField<DateTime>[] = [];
		const now = this.dependencies.timeService.now();

		for (let i = 0; i < this._availableDatesCount; i++) {
			const date = now.minus({ days: i });
			let formattedTitle: string = this.dependencies.timeService.formatDayAndMonth(date);

			if (i === 0) {
				formattedTitle = 'Today';
			} else if (i === 1) {
				formattedTitle = 'Yesterday';
			}

			selectItems.push({
				data: date,
				title: formattedTitle,
				value: this.getDateValueToStore(date)
			});
		}

		return selectItems;
	});

	public readonly progressValueFormInputs = computed<FormInput[]>(() => {
		const formInputs: FormInput[] = [];
		const currentDateMatcher: string = this.selectedDate();

		for (const assignedType of this.assignedUserProgressTypes()) {
			const progressType = this.allProgressTypes().find((m) => m.id === assignedType.progressTypeId);
			const valueForCurrentDay = assignedType.values.find((m) => m.date === currentDateMatcher)?.value;

			// prepare shown value
			let shownValue: number | undefined = undefined;

			// convert unit to preferred format
			const preferredValueResult = progressHelper.convertValueToPrefferedFormat({
				preferredUnit: this.authUser()?.preferredUnit,
				unit: progressType?.unit,
				value: valueForCurrentDay ?? 0,
				roundValue: true // round only for display
			});

			if (valueForCurrentDay === 0 || valueForCurrentDay) {
				shownValue = preferredValueResult.value;
			}

			formInputs.push({
				id: progressType?.id ?? guidHelper.newGuid(),
				name: super.getRequiredItemName(progressType?.system.name),
				subtitle: preferredValueResult.unit,
				value: signal(shownValue),
				assignedProgressType: assignedType
			});
		}

		return formInputs;
	});

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		return of(undefined).pipe(
			map(() => {
				this.save.emit(this.assignedUserProgressTypes());
			})
		);
	});

	constructor() {
		super();
	}

	handleValueChange(newValue: number | undefined, formInput: FormInput): void {
		const currentDateMatcher: ISODate = this.selectedDate();
		const assignedUserProgressType = this.assignedUserProgressTypes().find(
			(m) => m.id === formInput.assignedProgressType.id
		);

		if (assignedUserProgressType) {
			const progressType = this.allProgressTypes().find((m) => m.id === assignedUserProgressType?.progressTypeId);
			const valueForCurrentDay = assignedUserProgressType.values.find((m) => m.date === currentDateMatcher);

			// convert unit to preferred format so that we know what type th evalue is
			const preferredValueResult = progressHelper.convertValueToPrefferedFormat({
				preferredUnit: this.authUser()?.preferredUnit,
				unit: progressType?.unit,
				value: newValue ?? 0
			});

			// convert value back to metric system
			const valueInMetricSystem: number = progressHelper.convertValueToPrefferedFormat({
				preferredUnit: 'metric',
				unit: preferredValueResult.unit,
				value: newValue ?? 0
			}).value;

			if (valueForCurrentDay) {
				// update existing value
				valueForCurrentDay.value = valueInMetricSystem;
			} else {
				// add new value
				assignedUserProgressType.values.push({
					date: currentDateMatcher,
					value: valueInMetricSystem
				});
			}
		}
	}

	private getDateValueToStore(date: DateTime): ISODate {
		return this.dependencies.timeService.toISODate(date);
	}
}
